/**
 * list_search_controller.js — search and status filtering for list pages (Stimulus).
 *
 * Two modes, selected at runtime:
 *
 * LOCAL (preferred). When the element carries data-rows-url, the controller
 * fetches that fragment once after paint. It holds every row off-DOM in a
 * detached <template>, indexed by its text and its data-status, so typing in the
 * search box and clicking a status pill both filter in memory with no server
 * round-trip. Only matching rows are ever inserted, which keeps the live DOM
 * small no matter how many records the tenant has. The fragment is rendered by
 * the same Jinja partial as the page, so there is no second row renderer.
 *
 * SERVER (fallback). Used until the fragment lands, and permanently if the
 * endpoint declines it (204, tenant over the row cap). The GET form targets the
 * page's results <turbo-frame> (data-turbo-frame), so a search refreshes only
 * the frame — the input is never replaced, which keeps focus and the iOS
 * keyboard alive between keystrokes. data-turbo-action="replace" updates the URL
 * without stacking a history entry per keystroke. Status pills are plain links,
 * so in this mode a click simply navigates.
 *
 * Status matching is exact string equality on the row's data-status, which is
 * what the server does too (`Quote.status == status`). Filters that are not
 * status equality — Overdue on invoices, Recurring on jobs — carry no
 * data-status-key and stay on the server path.
 *
 * Hidden inputs on the form carry the active status/per_page filters; page is
 * intentionally omitted so a new search resets to page 1. An empty search box
 * un-names the input so the term is dropped from the query string entirely.
 *
 * The header count badge and the pagination strip both live where a local
 * filter would leave them lying: the badge is kept in sync from data-total
 * (server mode) or the match count (local mode), and the strip is hidden while
 * a local filter is active because its page links no longer describe the rows
 * on screen.
 *
 * Dashboard lists opt into data-local-only="true". With no search and no pill
 * they show only the short slice the server painted (DASHBOARD_ROWS in
 * application/web/views/pagination.py); the full row set stays in the off-DOM
 * index, so a phone never lays out hundreds of rows it was not asked for. Rows
 * marked data-search-priority="secondary" appear after primary matches only
 * while searching.
 *
 * Usage — the controller wraps BOTH the status filter group and the search form,
 * because a Stimulus action only fires for elements inside its controller:
 *
 *   <div data-controller="list-search" data-list-frame="quotes-list"
 *        data-rows-url="{{ url_for('quotes.quote_rows') }}"
 *        data-count-badge="quotes" data-active-status="{{ current_status or '' }}">
 *     {{ status_filter(...) }}
 *     <form class="table-search" method="get" action="{{ url_for(...) }}"
 *           data-turbo-frame="quotes-list" data-turbo-action="replace">
 *       <input type="text" name="search"
 *              data-list-search-target="input"
 *              data-action="input->list-search#search">
 *     </form>
 *   </div>
 *
 * The wrapper names the frame with data-list-frame, NOT data-turbo-frame: Turbo
 * resolves data-turbo-frame from the closest ancestor, so putting it here would
 * make the status pills target the frame in server mode and leave the stat cards
 * above the table stale. Only the form is a Turbo frame target.
 */
import { Controller } from "@hotwired/stimulus";

const DEBOUNCE_MS = 300;
// Beyond this many matches the list is not browsable anyway, and inserting them
// costs layout. Excess matches are reported in the count, not rendered.
const MAX_RENDERED_MATCHES = 200;
// The breakpoint below which the app shows its bottom nav instead of the desktop
// navbar (Bootstrap's lg). Above it there is no on-screen keyboard to dodge, and
// yanking the page on focus would be hostile.
const MOBILE = '(max-width: 991.98px)';

/**
 * Does every word of `term` appear in `text`, in any order? The server-side
 * search (application/services/entity_search.py) applies the same rule, so a
 * list answers alike whichever path filters it. Both arguments are lowercase.
 */
export function matchesEveryWord(text, term) {
    return term.split(/\s+/).every((word) => text.includes(word));
}

export default class extends Controller {
    static targets = ["input"];

    connect() {
        this._frame = document.getElementById(this.element.dataset.listFrame);
        this._form = this.element.querySelector('form.table-search');
        this._localOnly = this.element.dataset.localOnly === 'true';
        this._onFrameLoad = () => this._syncCount();
        if (this._frame) {
            this._frame.addEventListener('turbo:frame-load', this._onFrameLoad);
        }
        this._index = this._localOnly && this._frame
            ? this._buildIndex(this._frame.querySelectorAll('tbody tr:not(.empty-state-row)'))
            : null;
        this._serverRows = null;
        // The state the server rendered. Returning to it restores that markup
        // (and its pagination) rather than re-rendering from the index.
        this._initialStatus = this.element.dataset.activeStatus || '';
        // Resolves once the index is built or has definitively failed. A pill
        // clicked before the fragment lands awaits this rather than navigating.
        this._ready = this._loadRows();
    }

    disconnect() {
        clearTimeout(this._timer);
        if (this._rafId) cancelAnimationFrame(this._rafId);
        this._rafId = null;
        this._stopWatchingKeyboard();
        if (this._frame) {
            this._frame.removeEventListener('turbo:frame-load', this._onFrameLoad);
            this._frame = null;
        }
        this._form = null;
        this._index = null;
        this._serverRows = null;
    }

    // On a phone the keyboard covers the lower half of the screen, so a search
    // box sitting mid-page filters its rows into the half that just disappeared.
    // Pull the box to the top of the viewport, where the results have room.
    //
    // The browser scrolls a focused input into view by itself, but it stops as
    // soon as the input is barely visible — typically just above the keyboard,
    // the worst place. So we scroll again, twice: once now, and once after the
    // keyboard has actually settled. visualViewport's resize is that signal
    // (iOS never resizes the layout viewport for the keyboard, so a plain
    // resize listener would hear nothing). The second scroll is a no-op when
    // the first one already landed.
    reveal() {
        if (!window.matchMedia(MOBILE).matches) return;
        this._scrollSearchToTop();
        this._watchKeyboard();
    }

    _scrollSearchToTop() {
        if (!this.hasInputTarget) return;
        const box = this.inputTarget.closest('.table-search') || this.inputTarget;
        box.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    _watchKeyboard() {
        const viewport = window.visualViewport;
        if (!viewport || this._onViewportResize) return;
        this._onViewportResize = () => {
            const stillTyping = this.hasInputTarget && document.activeElement === this.inputTarget;
            this._stopWatchingKeyboard();
            // The keyboard closing resizes the viewport too. Only chase the box
            // back to the top if the box is still the thing being typed into.
            if (stillTyping) this._scrollSearchToTop();
        };
        viewport.addEventListener('resize', this._onViewportResize, { once: true });
    }

    _stopWatchingKeyboard() {
        if (!this._onViewportResize) return;
        window.visualViewport.removeEventListener('resize', this._onViewportResize);
        this._onViewportResize = null;
    }

    search(event) {
        const input = event.target;

        if (this._index) {
            clearTimeout(this._timer);
            this._scheduleFilter();
            return;
        }

        const term = input.value.trim();
        clearTimeout(this._timer);
        this._timer = setTimeout(() => {
            // Drop the param entirely when the box is emptied (an unnamed
            // input is excluded from GET serialization; disabling it instead
            // would steal focus and close the keyboard).
            input.name = term ? 'search' : '';
            if (this._form) this._form.requestSubmit();
        }, DEBOUNCE_MS);
    }

    // Filtering rebuilds the tbody, and layout of the result is charged to
    // whatever frame the work runs in. Called straight from the input handler it
    // lands in the same frame as the typed character, so the character cannot
    // paint until every matching row has been laid out — 1.7s for 184 rows on a
    // Pixel 5. Two frames of deferral put the keystroke in frame N and the rows
    // in frame N+1: the inner rAF is scheduled from inside the first callback,
    // which the browser only runs after frame N has painted.
    //
    // Repeat keystrokes coalesce onto the pending callback rather than queueing,
    // so holding a key filters once per frame against the latest value, not once
    // per character.
    _scheduleFilter() {
        if (this._rafId) return;
        this._rafId = requestAnimationFrame(() => {
            this._rafId = requestAnimationFrame(() => {
                this._rafId = null;
                this._applyFilter();
            });
        });
    }

    // Status pill click.
    //
    // preventDefault() is the FIRST statement and runs synchronously, before the
    // index check and before any await. By the time a promise resolves the click
    // has finished dispatching and Turbo has already decided to visit — so the
    // only place a navigation can be suppressed is here, unconditionally.
    //
    // The row fragment is fetched on idle and can take a second on a big tenant,
    // so a pill clicked during that window waits for it instead of falling
    // through to a page load. Navigation is then an explicit, deliberate act:
    // only if the index could not be built at all (fetch failed, or the tenant is
    // over the row cap and the endpoint declined).
    async filterStatus(event) {
        event.preventDefault();
        const link = event.currentTarget;

        try {
            if (!this._index) await this._ready;
            if (!this._index) {
                this._navigate(link.href);
                return;
            }

            this.element.dataset.activeStatus = link.dataset.statusKey || '';
            this._markActive(link);
            this._syncStatusParam();
            this._applyFilter();
        } catch (error) {
            console.warn('[list-search] local status filter failed, navigating', error);
            this._navigate(link.href);
        }
    }

    // We suppressed the link's own navigation, so re-issue it through Turbo
    // rather than a hard reload when the local path is genuinely unavailable.
    _navigate(href) {
        if (window.Turbo) {
            window.Turbo.visit(href);
        } else {
            window.location.assign(href);
        }
    }

    // Fetch every row once the page is idle, parse it off-DOM, and index each
    // row by its visible text and status. Any failure leaves _index null, which
    // keeps both the search box and the status pills on the server path.
    //
    // Returns a promise that resolves when the index is built or has failed —
    // never rejects, so awaiting it is always safe. The idle wait is inside the
    // promise, so a click during that window blocks on the whole load, not just
    // on the scheduling.
    _loadRows() {
        const url = this.element.dataset.rowsUrl;
        if (!url) return Promise.resolve();

        const load = async () => {
            try {
                const response = await fetch(url, {
                    headers: { 'Accept': 'text/html' },
                    credentials: 'same-origin',
                });
                if (response.status === 204 || !response.ok) return;

                const template = document.createElement('template');
                template.innerHTML = await response.text();
                const rows = Array.from(template.content.querySelectorAll('tr'));
                if (!rows.length) return;

                this._index = this._buildIndex(rows);
            } catch (error) {
                console.warn('[list-search] local index unavailable, using server filtering', error);
            }
        };

        return new Promise((resolve) => {
            const run = () => load().then(resolve);
            if (window.requestIdleCallback) {
                requestIdleCallback(run, { timeout: 2000 });
            } else {
                setTimeout(run, 200);
            }
        });
    }

    _buildIndex(rows) {
        return Array.from(rows).map((row) => ({
            row,
            status: row.dataset.status || '',
            priority: row.dataset.searchPriority || 'primary',
            text: (row.textContent || '').toLowerCase().replace(/\s+/g, ' '),
        }));
    }

    // The single place both the search term and the status pill are applied.
    _applyFilter() {
        const tbody = this._frame && this._frame.querySelector('tbody');
        if (!tbody) return;

        // Snapshot the server-rendered page before the first local edit.
        if (!this._serverRows) {
            this._serverRows = Array.from(tbody.children);
        }

        const term = this.hasInputTarget ? this.inputTarget.value.trim().toLowerCase() : '';
        const status = this.element.dataset.activeStatus || '';

        if (!term && status === this._initialStatus) {
            this._swapRows(tbody, ...this._serverRows);
            this._setCount(tbody.dataset.total || this._serverRows.length);
            this._showPagination(true);
            this._notifyRowsUpdated(false);
            return;
        }

        const matches = this._index.filter((entry) => {
            const matchesTerm = term
                ? matchesEveryWord(entry.text, term)
                : entry.priority !== 'secondary';
            return matchesTerm && (!status || entry.status === status);
        });
        const orderedMatches = [
            ...matches.filter((entry) => entry.priority !== 'secondary'),
            ...matches.filter((entry) => entry.priority === 'secondary'),
        ];

        // Move the indexed rows rather than deep-copying them. The index holds
        // the only reference, so appending relocates a row into the tbody and
        // replaceChildren pulls it back out on the next keystroke — no node is
        // ever lost, and a 184-match filter stops paying for 184 cloneNode(true)
        // subtree copies. Cloning also silently detached any listener or
        // Stimulus controller bound to the original row.
        const fragment = document.createDocumentFragment();
        for (const entry of orderedMatches.slice(0, MAX_RENDERED_MATCHES)) {
            fragment.appendChild(entry.row);
        }
        this._swapRows(tbody, fragment);
        this._setCount(matches.length);
        this._showPagination(false);
        this._notifyRowsUpdated(Boolean(term));
    }

    // Replace the tbody's rows with the entrance animation stood down.
    // turbo-animations.js fades in every <tr> added to a tbody[id] — right for a
    // Turbo Stream appending one row, wrong here: a bulk local re-render fading
    // every row reads as a full page reload. The marker is read by the observer's
    // microtask, which is guaranteed to run before the timeout clears it.
    _swapRows(tbody, ...nodes) {
        tbody.dataset.bulkRender = 'true';
        tbody.replaceChildren(...nodes);
        setTimeout(() => { delete tbody.dataset.bulkRender; }, 0);
    }

    _notifyRowsUpdated(searchActive) {
        if (!this._frame) return;
        this._frame.dataset.searchActive = searchActive ? 'true' : 'false';
        this._frame.dispatchEvent(new CustomEvent('list-search:updated'));
    }

    _markActive(link) {
        const group = link.closest('.btn-group');
        if (!group) return;
        for (const anchor of group.querySelectorAll('a')) {
            anchor.classList.toggle('active', anchor === link);
        }
    }

    // Keep the address bar honest without a navigation, so a refresh or a
    // shared link lands on the same filter the user is looking at.
    _syncStatusParam() {
        const status = this.element.dataset.activeStatus;
        const url = new URL(window.location.href);
        if (status) {
            url.searchParams.set('status', status);
        } else {
            url.searchParams.delete('status');
        }
        url.searchParams.delete('page');
        window.history.replaceState(window.history.state, '', url);
    }

    // The strip's page links describe the server's result set, not the filtered
    // one, so it is hidden rather than left to lie. d-none, not the hidden
    // attribute: the strip is a .d-flex, and Bootstrap emits .d-none after both
    // .d-flex and [hidden], so only .d-none actually wins the cascade.
    _showPagination(visible) {
        if (!this._frame) return;
        const strip = this._frame.querySelector('.list-pagination');
        if (strip) strip.classList.toggle('d-none', !visible);
    }

    _syncCount() {
        if (!this._frame) return;
        const total = this._frame.querySelector('[data-total]');
        if (total) this._setCount(total.dataset.total);
    }

    _setCount(value) {
        const badgeKey = this.element.dataset.countBadge;
        if (!badgeKey) return;
        const badge = document.querySelector('[data-count-target="' + badgeKey + '"]');
        if (badge) badge.textContent = value;
    }
}
